import {
    mdiChevronRight,
    mdiClipboardTextOutline,
} from '@mdi/js';
import Icon from '@mdi/react';
import _ from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MessagingContext } from '../../../services';
import { formatDate } from '../../../services/primitives/date-utils';
import { Variables } from '../../base';
import { fetchClaimSummariesFromLatestHl7, IClaimHl7Summary } from '../claim-hl7-summary';
import * as S from './claim-card.styles';

export interface IClaimCardProps {
    claimIds: string[];
    messageId?: string;
}

const formatAmount = (amount: IClaimHl7Summary['chargeAmount']): string => {
    if (!amount || !amount.amount) {
        return '$0.00';
    }
    const value = parseFloat(amount.amount);
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: amount.currency || 'USD',
    }).format(value);
};

const getPatientName = (claim: IClaimHl7Summary): string => {
    const firstName = claim.patientFirst || '';
    const lastName = claim.patientLast || '';
    return `${firstName} ${lastName}`.trim() || 'Unknown';
};

export const ClaimCard: React.FunctionComponent<IClaimCardProps> = ({ claimIds, messageId }) => {
    const context = useContext(MessagingContext);
    const { accountId } = useParams<{ accountId?: string }>();
    const isSupportApp = context.account?.supportGrant === true;

    const [claims, setClaims] = useState<IClaimHl7Summary[]>([]);
    const [loading, setLoading] = useState(true);

    const claimIdsKey = useMemo(
        () => (claimIds?.length ? [...claimIds].sort().join(',') : ''),
        [claimIds],
    );

    useEffect(() => {
        const fetchClaims = async () => {
            if (!claimIds || claimIds.length === 0) {
                setClaims([]);
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const summaries = await fetchClaimSummariesFromLatestHl7(claimIds, isSupportApp);
                setClaims(summaries);
            } catch (error) {
                console.error('Failed to fetch claim HL7 summaries:', error);
                setClaims([]);
            } finally {
                setLoading(false);
            }
        };

        fetchClaims();
    }, [claimIdsKey, isSupportApp, messageId]);

    const openClaim = (claimId: string) => {
        const accId = accountId || context.account?.grantedAccountId;
        if (!accId || !claimId) {
            return;
        }
        window.open(`/${accId}/claims/${claimId}`, '_blank', 'noopener,noreferrer');
    };

    const total = useMemo(() => {
        const sum = claims.reduce((acc, claim) => {
            const value = claim.chargeAmount?.amount ? parseFloat(claim.chargeAmount.amount) : 0;
            return acc + value;
        }, 0);
        const currency = claims[0]?.chargeAmount?.currency || 'USD';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
        }).format(sum);
    }, [claims]);

    if (loading) {
        return (
            <S.Section>
                <S.SectionLabel>Claim</S.SectionLabel>
                <S.LoadingState>Loading claims...</S.LoadingState>
            </S.Section>
        );
    }

    if (!claims.length) {
        return null;
    }

    const multi = claims.length > 1;
    const primary = claims[0];

    return (
        <S.Section>
            <S.SectionLabel>{multi ? 'Claims' : 'Claim'}</S.SectionLabel>
            <S.Card>
                <S.CardHeader>
                    <Icon path={mdiClipboardTextOutline} size={0.75} color={Variables.ThemeLight.TextMedium} />
                    <S.PatientName>{getPatientName(primary)}</S.PatientName>
                    {primary.patientDob && (
                        <S.PatientMeta className="tabular">
                            DOB {formatDate(primary.patientDob, 'yyyy/MM/dd')}
                        </S.PatientMeta>
                    )}
                    {multi ? (
                        <S.MultiBadge className="tabular">{claims.length} claims</S.MultiBadge>
                    ) : (
                        primary.id && (
                            <S.OpenLink type="button" variant="unstyled" onClick={() => openClaim(primary.id!)}>
                                Open claim
                                <Icon path={mdiChevronRight} size={0.65} />
                            </S.OpenLink>
                        )
                    )}
                </S.CardHeader>

                {multi ? (
                    <>
                        {claims.map(claim => (
                            <S.ClaimRow key={claim.id}>
                                <S.ClaimRowField $flex="0 0 92px">
                                    <S.MetricLabel>DOS</S.MetricLabel>
                                    <S.MetricValue className="tabular">
                                        {claim.fromDos ? formatDate(claim.fromDos, 'MMM dd yyyy') : '—'}
                                    </S.MetricValue>
                                </S.ClaimRowField>
                                <S.ClaimRowField>
                                    <S.MetricLabel>Claim Control Number</S.MetricLabel>
                                    <S.ClaimIdValue
                                        className="tabular"
                                        title={claim.controlNumber || claim.id}>
                                        {claim.controlNumber || claim.id || '—'}
                                    </S.ClaimIdValue>
                                </S.ClaimRowField>
                                <S.ClaimRowField $flex="none">
                                    <S.MetricLabel>Charge</S.MetricLabel>
                                    <S.MetricValue className="tabular">
                                        {formatAmount(claim.chargeAmount)}
                                    </S.MetricValue>
                                </S.ClaimRowField>
                                {claim.id && (
                                    <S.RowOpenButton
                                        type="button"
                                        variant="unstyled"
                                        aria-label="Open claim"
                                        onClick={() => openClaim(claim.id!)}>
                                        <Icon path={mdiChevronRight} size={0.7} />
                                    </S.RowOpenButton>
                                )}
                            </S.ClaimRow>
                        ))}
                        <S.Footer>
                            <S.FooterMeta>
                                {claims.length} claims
                                {_.uniq(_.compact(_.map(claims, 'primaryPayerName'))).length > 0
                                    ? ` · ${_.uniq(_.compact(_.map(claims, 'primaryPayerName'))).join(', ')}`
                                    : ''}
                            </S.FooterMeta>
                            <S.FooterTotal className="tabular">{total}</S.FooterTotal>
                        </S.Footer>
                    </>
                ) : (
                    <S.MetricsRow>
                        {[
                            ['DOS', primary.fromDos ? formatDate(primary.fromDos, 'MMM dd yyyy') : '—'],
                            ['Payer', primary.primaryPayerName || '—'],
                            ['Charge', formatAmount(primary.chargeAmount)],
                        ].map(([label, value], index) => (
                            <S.MetricCell key={label} $borderLeft={index > 0}>
                                <S.MetricLabel>{label}</S.MetricLabel>
                                <S.MetricValue className="tabular" title={value}>
                                    {value}
                                </S.MetricValue>
                            </S.MetricCell>
                        ))}
                    </S.MetricsRow>
                )}
            </S.Card>
        </S.Section>
    );
};
